'use client';

import { useTranslations } from 'next-intl';
import type { PlayerStanding } from '@/draw/types';
import type { ShortNamed } from '../shortName';

interface ChampionRevealProps {
  champion: PlayerStanding & ShortNamed;
  focusedPlayer: (PlayerStanding & ShortNamed) | null;
  isDesktop?: boolean;
}

function getOrdinal(
  position: number,
  t: ReturnType<typeof useTranslations<'Celebration'>>,
): string {
  if (position === 1) return t('positionOrdinal1');
  if (position === 2) return t('positionOrdinal2');
  if (position === 3) return t('positionOrdinal3');
  return t('positionOrdinalN', { position });
}

const GOLD = '#F5A623';

export function ChampionReveal({
  champion,
  focusedPlayer,
  isDesktop = false,
}: ChampionRevealProps) {
  const t = useTranslations('Celebration');

  const isPersonalized = focusedPlayer && focusedPlayer.playerId !== champion.playerId;
  const heroPlayer = isPersonalized ? focusedPlayer : champion;
  const isUndefeated = champion.losses === 0 && champion.wins > 0;

  // Split a personal name for the two-line treatment (given names / surname).
  // A team label (shortName === playerName) is free text: keep it whole on the
  // headline line rather than chopping "FC Thunder Cats" into "FC Thunder" / "Cats".
  const isTeamLabel =
    heroPlayer.shortName !== undefined && heroPlayer.shortName === heroPlayer.playerName;
  const nameParts = heroPlayer.playerName.split(' ');
  const firstLine = isPersonalized
    ? getOrdinal(heroPlayer.position, t)
    : isTeamLabel
      ? ''
      : nameParts.slice(0, -1).join(' ') || nameParts[0];
  const secondLine =
    isPersonalized || isTeamLabel ? heroPlayer.playerName : nameParts[nameParts.length - 1];

  return (
    <div className={isDesktop ? 'text-left' : 'text-center'}>
      {/* Eyebrow */}
      <div className={`mb-4 flex items-center gap-3 ${isDesktop ? '' : 'justify-center'}`}>
        <div className="h-1 w-10 rounded-full" style={{ background: GOLD }} />
        <span
          className="text-[12px] font-black tracking-[5px] uppercase lg:text-[13px]"
          style={{ color: GOLD }}
        >
          {isPersonalized ? t('youFinished') : t('boxChampion')}
        </span>
      </div>

      {/* THE NAME */}
      <h1>
        <span
          className="block leading-[0.88] tracking-[0.02em] text-white uppercase"
          style={{
            fontFamily: 'var(--font-anton)',
            fontSize: isDesktop ? 'clamp(80px, 9vw, 120px)' : 'clamp(48px, 12vw, 72px)',
          }}
        >
          {firstLine}
        </span>
        <span
          className="block leading-[0.88] tracking-[0.02em] uppercase"
          style={{
            fontFamily: 'var(--font-anton)',
            fontSize: isDesktop ? 'clamp(72px, 8vw, 100px)' : 'clamp(40px, 11vw, 64px)',
            color: GOLD,
          }}
        >
          {secondLine}
        </span>
      </h1>

      {/* Record */}
      {!isPersonalized && (
        <div className={`mt-5 flex items-baseline gap-4 ${isDesktop ? '' : 'justify-center'}`}>
          <span
            style={{ fontFamily: 'var(--font-anton)', letterSpacing: '0.06em' }}
            className="text-[40px] text-white lg:text-[56px]"
          >
            {champion.wins}
          </span>
          <span
            style={{ fontFamily: 'var(--font-anton)' }}
            className="text-[40px] text-white/20 lg:text-[56px]"
          >
            –
          </span>
          <span
            style={{ fontFamily: 'var(--font-anton)', letterSpacing: '0.06em' }}
            className={`text-[40px] lg:text-[56px] ${isUndefeated ? 'text-emerald-400' : 'text-white'}`}
          >
            {champion.losses}
          </span>
          {isUndefeated && (
            <span className="rounded-md border-2 border-emerald-400/30 px-4 py-1.5 text-[11px] font-black tracking-[2px] text-emerald-400 uppercase lg:text-[12px]">
              {t('undefeated')}
            </span>
          )}
        </div>
      )}

      {/* Secondary champion callout (personalized view) */}
      {isPersonalized && (
        <div className={`mt-6 flex items-center gap-3 ${isDesktop ? '' : 'justify-center'}`}>
          <span className="text-2xl">🏆</span>
          <div>
            <div className="text-[9px] font-bold tracking-[2px] text-white/30 uppercase">
              {t('champion')}
            </div>
            <div className="text-sm font-black" style={{ color: GOLD }}>
              {champion.playerName}
            </div>
          </div>
          <div className="ml-2 text-sm font-black text-white/60">
            {champion.wins}W–{champion.losses}L
          </div>
        </div>
      )}
    </div>
  );
}
